import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'
import { zeroAddress } from 'viem'

import { FormatOptions, FormatUSDOptions } from '../token-repository/types'
import { findSignificantPrecision } from '@/domain/common/format'
import { TokenDefinition, TokenDefinitionConstructorParams } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

const ZERO_PRICE_FORMAT_PLACEHOLDER = '$ N/A'
const FALLBACK_FORMAT_PRECISION = 4

export interface TokenConstructorParams extends TokenDefinitionConstructorParams {
  unitPriceUsd: NormalizedNumber
}

export class Token extends TokenDefinition {
  readonly unitPriceUsd: NormalizedNumber

  constructor(params: TokenConstructorParams) {
    super(params)
    this.unitPriceUsd = params.unitPriceUsd
  }

  toUSD(amount: NormalizedNumber): NormalizedNumber {
    return amount.times(this.unitPriceUsd)
  }

  formatUSD(amount: NormalizedNumber, options: FormatUSDOptions = {}): string {
    return formatUSD(amount, this.unitPriceUsd, options)
  }

  format(amount: NormalizedNumber, options: FormatOptions = {}): string {
    if (options.style === 'auto') {
      return formatAuto(amount, this.unitPriceUsd, 'cents')
    }

    if (options.style === 'auto-precision-1-usd') {
      return formatAuto(amount, this.unitPriceUsd, 'dollars')
    }

    return formatCompact(amount)
  }

  formatBalance(balance: NormalizedNumber): string {
    return formatAuto(balance, this.unitPriceUsd)
  }

  override clone(overrides: {
    address?: CheckedAddress
    symbol?: TokenSymbol
    name?: string
    decimals?: number
    logo?: string
    unitPriceUsd?: NormalizedNumber
  }): Token {
    return new Token({
      address: overrides.address ?? this.address,
      symbol: overrides.symbol ?? this.symbol,
      name: overrides.name ?? this.name,
      decimals: overrides.decimals ?? this.decimals,
      logo: overrides.logo ?? this.logo,
      unitPriceUsd: overrides.unitPriceUsd ?? this.unitPriceUsd,
    })
  }
}

function formatUSD(
  amount: NormalizedNumber,
  unitPriceUsd: NormalizedNumber,
  { compact = false, showCents = 'always' }: FormatUSDOptions = {},
): string {
  if (unitPriceUsd.isZero()) {
    return ZERO_PRICE_FORMAT_PLACEHOLDER
  }

  const usdValue = amount.times(unitPriceUsd)
  if (amount.gt(0) && usdValue.lt(0.01)) {
    return '<$0.01'
  }

  if (compact && usdValue.gte(1000)) {
    return `$${formatCompact(usdValue)}`
  }

  const fractionDigitsConfig = {
    always: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    never: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
    'when-not-round': { minimumFractionDigits: amount.isInteger() ? 0 : 2, maximumFractionDigits: 2 },
  } satisfies Record<string, { minimumFractionDigits: number; maximumFractionDigits: number }>

  const usdFormatter = getNumberFormatter({
    style: 'currency',
    currency: 'USD',
    ...fractionDigitsConfig[showCents],
  })
  return usdFormatter.format(usdValue.toNumber())
}

function formatAuto(
  value: NormalizedNumber,
  unitPriceUsd: NormalizedNumber,
  valuePrecision: 'dollars' | 'cents' = 'cents',
): string {
  const precision = unitPriceUsd.isZero()
    ? FALLBACK_FORMAT_PRECISION
    : findSignificantPrecision(unitPriceUsd, valuePrecision === 'dollars' ? 0 : 2)
  const leastSignificantValue = BigNumber(1).shiftedBy(-precision)
  const rounded = BigNumber(value.toFixed(precision))
  if (value.gt(0) && rounded.lt(leastSignificantValue)) {
    return `<${leastSignificantValue.toFixed()}`
  }

  const formatter = getNumberFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.max(precision, 2),
  })

  return formatter.format(value.toNumber())
}

function formatCompact(value: NormalizedNumber): string {
  const n = value.toNumber()
  if (n === 0) return '0'
  if (n < 0.001) return '<0.001'

  const formatterOptions = getFormatterOptions(n)
  return getNumberFormatter(formatterOptions).format(n)
}

function countSignificantDigits(n: number): number {
  const totalDigits = Math.floor(Math.log10(Math.abs(n))) + 1
  const significantDigits = totalDigits % 3 || 3
  return significantDigits
}

function getNumberFormatter(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  return new Intl.NumberFormat('en-US', options)
}

function getFormatterOptions(n: number): Intl.NumberFormatOptions {
  if (n < 1) {
    return { maximumFractionDigits: 3 }
  }
  if (n >= 1000 && n < 10_000) {
    return {
      maximumFractionDigits: 0,
    }
  }
  return {
    notation: 'compact',
    maximumFractionDigits: Math.max(0, 4 - countSignificantDigits(n)),
  }
}

export const USD_MOCK_TOKEN = new Token({
  address: CheckedAddress(zeroAddress),
  symbol: TokenSymbol('USD'),
  name: 'US Dollar',
  decimals: 2,
  logo: '',
  unitPriceUsd: NormalizedNumber(1),
})

export function createToken(
  token:
    | TokenDefinition
    | Token
    | { symbol: TokenSymbol; name: string; decimals: number; address: CheckedAddress; logo: string },
  unitPriceUsd: NormalizedNumber,
): Token {
  return new Token({
    symbol: token.symbol,
    name: token.name,
    decimals: token.decimals,
    address: token.address,
    logo: token.logo,
    unitPriceUsd,
  })
}
